import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { 
  TimeFormatEnum,
  EntityTerminology,
  formatEntityTerm,
} from '@scrambl/shared';

// Helper functions for time formatting
const formatTimeAsDecimal = (hours: number): string => {
  return hours.toFixed(2);
};

const formatTimeAsHHMM = (hours: number): string => {
  const totalMinutes = Math.round(hours * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
};

const parseDecimalTime = (timeString: string): number => {
  const num = parseFloat(timeString);
  return isNaN(num) ? 0 : num;
};

const parseHHMMTime = (timeString: string): number => {
  const trimmed = timeString.trim();
  if (!trimmed) return 0;

  const hasColon = trimmed.includes(':');
  const hasDot = trimmed.includes('.');

  if (hasColon) {
    // Colon separator: treat as H:MM (e.g. "1:30" → 1.5h, "0:45" → 0.75h)
    const parts = trimmed.split(':');
    const hours = parts[0] ? parseInt(parts[0], 10) : 0;
    const minutes = parts[1] ? parseInt(parts[1], 10) : 0;
    if (isNaN(hours) || isNaN(minutes)) return 0;
    return hours + (minutes / 60);
  }

  if (hasDot) {
    // Dot separator: treat as decimal hours (e.g. "1.5" → 1.5h = 1:30, ".75" → 0.75h = 0:45)
    const num = parseFloat(trimmed);
    return isNaN(num) ? 0 : num;
  }

  // No separator — treat as hours (e.g. "2" → 2h)
  const num = parseFloat(trimmed);
  return isNaN(num) ? 0 : num;
};

// UI State only - helper methods that operate on effective settings from query
interface OrganizationSettingsState {
  // Helper methods for time formatting (requires effectiveSettings from query)
  getTimeFormat: (effectiveSettings?: any) => TimeFormatEnum;
  formatTime: (hours: number, effectiveSettings?: any) => string;
  parseTime: (timeString: string, effectiveSettings?: any) => number;
  
  // Entity terminology helpers (requires effectiveSettings from query)
  getEntityTerminology: (effectiveSettings?: any) => EntityTerminology;
  getEntityTerm: (effectiveSettings?: any, plural?: boolean, capitalize?: boolean, articlePrefix?: boolean) => string;
  
  // Utilities
  reset: () => void;
}

export const useOrganizationSettingsStore = create<OrganizationSettingsState>()(
  persist(
    (set) => ({
      // Helper methods that require effectiveSettings to be passed in
      getTimeFormat: (effectiveSettings?: any) => {
        return effectiveSettings?.general?.timeFormat || TimeFormatEnum.DECIMAL;
      },

      formatTime: (hours: number, effectiveSettings?: any) => {
        const timeFormat = effectiveSettings?.general?.timeFormat || TimeFormatEnum.DECIMAL;
        return timeFormat === TimeFormatEnum.HH_MM 
          ? formatTimeAsHHMM(hours) 
          : formatTimeAsDecimal(hours);
      },

      parseTime: (timeString: string, effectiveSettings?: any) => {
        const timeFormat = effectiveSettings?.general?.timeFormat || TimeFormatEnum.DECIMAL;
        return timeFormat === TimeFormatEnum.HH_MM 
          ? parseHHMMTime(timeString) 
          : parseDecimalTime(timeString);
      },
      
      // Entity terminology helpers
      getEntityTerminology: (effectiveSettings?: any): EntityTerminology => {
        const terminology = effectiveSettings?.general?.entityTerminology;
        if (terminology && terminology.singular && terminology.plural) {
          return terminology as EntityTerminology;
        }
        return { singular: 'client', plural: 'clients' };
      },
      
      getEntityTerm: (effectiveSettings?: any, plural = false, capitalize = false, articlePrefix = false) => {
        const terminology = effectiveSettings 
          ? (useOrganizationSettingsStore.getState().getEntityTerminology(effectiveSettings))
          : { singular: 'client', plural: 'clients' };
        const term = plural ? terminology.plural : terminology.singular;
        return formatEntityTerm(term, capitalize, articlePrefix);
      },

      reset: () => {
        // No state to reset
      },
    }),
    {
      name: 'organization-settings-storage',
      partialize: () => ({}), // No state to persist
    }
  )
);
